import { Helmet } from "react-helmet-async";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import {
  Star,
  TrendingUp,
  MessageCircle,
  Shield,
  BarChart3,
  Users,
  Phone,
  CheckCircle,
  Award,
  Target,
  Stethoscope
} from "lucide-react";
import { useState } from "react";

const HealthcareLanding = () => {
  const [currency, setCurrency] = useState<"INR" | "USD">("INR");

  const plans = {
    INR: [
      {
        name: "Basic Plan",
        price: "₹0",
        period: "/14 Days Free Trial",
        popular: false,
        cta: "Get Started",
        features: [
          "Profile Setup",
          "Post Scheduling",
          "Smart Recommendations",
          "Auto Review Response",
          "Sentiment Analysis",
          "Analytics & Progress Reporting",
          "Email Support"
        ]
      },
      {
        name: "Essential Plan",
        price: "₹4,999",
        period: "/month",
        popular: true,
        cta: "Get Started",
        tagline: "Great for First-Time Users",
        features: [
          "All Basic Plan Benefits",
          "Competitor Analysis",
          "Rank Tracker - 2 Keywords",
          "Website SEO - One Time",
          "Weekly Reports Sharing",
          "Credit Card Accepted",
          "WhatsApp Support"
        ]
      },
      {
        name: "Growth Plan",
        price: "₹49,999",
        period: "/yearly",
        popular: true,
        savings: "17% OFF - Save ₹9,989/year",
        cta: "Get Started",
        features: [
          "All Essential Plan Benefits",
          "Posting with Branded Creatives",
          "Rank Tracker - 10 Keywords",
          "Website SEO - Quarterly",
          "Keyword Research",
          "MicroSite - FREE (₹10k value)",
          "Dedicated Expert Support"
        ]
      }
    ],
    USD: [
      {
        name: "Basic Plan",
        price: "$0",
        period: "/14 Days Free Trial",
        popular: false,
        cta: "Get Started",
        features: [
          "Profile Setup",
          "Post Scheduling",
          "Smart Recommendations",
          "Auto Review Response",
          "Sentiment Analysis",
          "Analytics & Progress Reporting",
          "Email Support"
        ]
      },
      {
        name: "Essential Plan",
        price: "$79",
        period: "/month",
        popular: true,
        cta: "Get Started",
        tagline: "Great for First-Time Users",
        features: [
          "All Basic Plan Benefits",
          "Competitor Analysis",
          "Rank Tracker - 2 Keywords",
          "Website SEO - One Time",
          "Weekly Reports Sharing",
          "Credit Card Accepted",
          "WhatsApp Support"
        ]
      },
      {
        name: "Growth Plan",
        price: "$799",
        period: "/yearly",
        popular: true,
        savings: "17% OFF - Save $149/year",
        cta: "Get Started",
        features: [
          "All Essential Plan Benefits",
          "Posting with Branded Creatives",
          "Rank Tracker - 10 Keywords",
          "Website SEO - Quarterly",
          "Keyword Research",
          "MicroSite - FREE (₹10k value)",
          "Dedicated Expert Support"
        ]
      }
    ]
  };

  const clientLogos = [
    { name: "Snore Care Clinic", logo: "/lovable-uploads/c-1.png" },
    { name: "Saroj", logo: "/lovable-uploads/c-2.png" },
    { name: "GoBest Dentist", logo: "/lovable-uploads/c-3.png" },
    { name: "Nidan Diagnostics", logo: "/lovable-uploads/c-4.png" },
    { name: "Osteon Orthopedic Hospital", logo: "/lovable-uploads/c-5.png" },
    { name: "Dr Tayade's Pathlab", logo: "/lovable-uploads/c-6.png" },
    { name: "Dr Muttha's", logo: "/lovable-uploads/c-7.png" },
    { name: "Sai Arogyam Dental Clinic and Implant Centre", logo: "/lovable-uploads/c-8.png" },
    { name: "Fab Skin Studio", logo: "/lovable-uploads/c-9.png" },
    { name: "Belle Cosmetic Clinic", logo: "/lovable-uploads/c-10.png" },
    { name: "Ear Nose Throat DXB", logo: "/lovable-uploads/c-11.png" },
    { name: "Ivory Dental Clinic and Implant Center", logo: "/lovable-uploads/c-12.png" },
    { name: "Ekdant Multispeciality Dental Clinic N Wellness Centre", logo: "/lovable-uploads/c-14.png" },
    // This mark carries no readable business name, so it gets an empty alt below
    // rather than a guessed one. The section heading still names what the strip is.
    { name: "", logo: "/lovable-uploads/c-15.png" },
    { name: "Mrudul Hearing Aid Centre", logo: "/lovable-uploads/c-16.png" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Google My Business Services for Healthcare Clinics | Fly Social</title>
        <meta
          name="description"
          content="Rank higher, get more calls, and grow without ads. Best Google My Business services for doctors and healthcare clinics. Get 30% more patients."
        />
        <meta
          name="keywords"
          content="Google My Business for doctors, healthcare local SEO, clinic GMB optimization, patient acquisition, medical practice marketing"
        />
        <link rel="canonical" href="https://www.fly-social.com/healthcare" />

        {/* Open Graph meta tags for social sharing */}
        <meta property="og:title" content="Google My Business Services for Healthcare Clinics | Fly Social" />
        <meta
          property="og:description"
          content="Rank higher, get more calls, and grow without ads. Best Google My Business services for doctors and healthcare clinics. Get 30% more patients."
        />
        <meta property="og:image" content="https://www.fly-social.com/lovable-uploads/healthcare-hero.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Get 30% More Patients - Google Profile Optimisation for Healthcare Clinics" />
        <meta property="og:url" content="https://www.fly-social.com/healthcare" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Fly Social" />

        {/* Twitter Card meta tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Google My Business Services for Healthcare Clinics | Fly Social" />
        <meta
          name="twitter:description"
          content="Rank higher, get more calls, and grow without ads. Best Google My Business services for doctors and healthcare clinics. Get 30% more patients."
        />
        <meta name="twitter:image" content="https://www.fly-social.com/lovable-uploads/healthcare-hero.png" />
        <meta name="twitter:image:alt" content="Get 30% More Patients - Google Profile Optimisation for Healthcare Clinics" />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Google My Business Services for Healthcare Clinics",
          "description": "Professional Google Business Profile optimization services for doctors, dentists, and healthcare clinics. Boost local rankings, manage reviews, and attract more patients.",
          "provider": {
            "@type": "Organization",
            "name": "Fly Social",
            "url": "https://www.fly-social.com"
          },
          "serviceType": "Local SEO and Google Business Profile Management",
          "areaServed": {
            "@type": "Country",
            "name": "India"
          },
          "url": "https://www.fly-social.com/healthcare",
          "offers": [
            {
              "@type": "Offer",
              "name": "Basic Plan",
              "price": "0",
              "priceCurrency": "INR",
              "description": "14 Days Free Trial - Profile Setup, Post Scheduling, Auto Review Response"
            },
            {
              "@type": "Offer",
              "name": "Essential Plan",
              "price": "4999",
              "priceCurrency": "INR",
              "description": "Monthly plan with competitor analysis, rank tracking, and WhatsApp support"
            },
            {
              "@type": "Offer",
              "name": "Growth Plan",
              "price": "49999",
              "priceCurrency": "INR",
              "description": "Yearly plan with branded creatives, keyword research, and dedicated support"
            }
          ]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What exactly does Fly Social do for my Google Business Profile?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We optimize your profile, manage posts and reviews, track rankings, and provide actionable insights to help you attract more patients from Google Search and Maps."
              }
            },
            {
              "@type": "Question",
              "name": "Can this actually help me get more patients?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes! Our clients typically see 20-40% more calls and inquiries within 60 days. Better rankings, professional review responses, and regular updates help patients find and trust your clinic."
              }
            },
            {
              "@type": "Question",
              "name": "Will I still have control over my Google profile?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Absolutely. You retain full ownership and control. We work with your approval and keep you informed of all changes and optimizations we make."
              }
            },
            {
              "@type": "Question",
              "name": "How does Fly Social reply to reviews?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We use AI-powered responses customized to your brand voice. Every reply is professional, empathetic, and designed to build trust with potential patients while addressing feedback constructively."
              }
            }
          ]
        })}</script>
      </Helmet>
      <NavBar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-purple-700 via-purple-600 to-purple-700 text-white py-12 sm:py-16 md:py-20 pt-24 sm:pt-28 md:pt-32 lg:pt-36 overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
            {/* Left Content */}
            <div className="lg:w-1/2 text-center lg:text-left">
              {/* Rating Badge */}
              <div className="inline-flex items-center gap-1 sm:gap-2 mb-4 sm:mb-6">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 sm:w-6 sm:h-6 fill-yellow-400 text-yellow-400" />
                ))}
                <span className="text-base sm:text-xl font-semibold ml-2">Rated 4.9/5</span>
              </div>

              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 sm:mb-8 leading-tight">
                Rank higher.
                <br />
                Get more calls.
                <br />
                Grow without ads.
              </h1>

              {/* Hero Image - Mobile Only (appears after title on mobile) */}
              <div className="block lg:hidden w-full mb-6 sm:mb-8">
                <div className="relative rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden max-w-lg mx-auto">
                  <img
                    src="/lovable-uploads/healthcare-hero.png"
                    alt="Get 30% More Patients - Google Profile Optimisation for Healthcare Clinics"
                    className="w-full h-auto"
                  />
                </div>
              </div>

              {/* Value Proposition Badges */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6 sm:mb-8 justify-center lg:justify-start">
                <div className="inline-flex items-center gap-2 sm:gap-3 bg-white/10 backdrop-blur-sm rounded-xl px-4 sm:px-5 py-2 sm:py-3 border border-white/20">
                  <Stethoscope className="w-5 h-5 sm:w-6 sm:h-6 text-green-300 flex-shrink-0" />
                  <span className="font-semibold text-sm sm:text-lg">Your Growth, Our Mission</span>
                </div>
                <div className="inline-flex items-center gap-2 sm:gap-3 bg-green-500/20 backdrop-blur-sm rounded-xl px-4 sm:px-5 py-2 sm:py-3 border border-green-300/30">
                  <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-green-300 flex-shrink-0" />
                  <span className="font-bold text-base sm:text-xl">+30% More Patients</span>
                </div>
              </div>

              <p className="text-base sm:text-xl mb-6 sm:mb-8 opacity-90">
                Best Google My Business Services for Doctors.
              </p>

              <Button
                size="lg"
                className="bg-purple-900 hover:bg-purple-800 text-white font-bold px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg rounded-full shadow-lg w-full sm:w-auto"
                onClick={() => {
                  if (typeof window !== 'undefined') {
                    document.querySelector('[data-section="contact-form"]')?.scrollIntoView({
                      behavior: 'smooth'
                    });
                  }
                }}
              >
                Get in touch →
              </Button>
            </div>

            {/* Right Image - Desktop Only */}
            <div className="hidden lg:block lg:w-1/2">
              <div className="relative rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden max-w-lg mx-auto lg:max-w-none">
                <img
                  src="/lovable-uploads/healthcare-hero.png"
                  alt="Get 30% More Patients - Google Profile Optimisation for Healthcare Clinics"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Clients Section */}
      <section className="py-10 sm:py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12">Loved by Clinics & Businesses</h2>
          <div className="max-w-6xl mx-auto">
            <Carousel
              opts={{
                align: "start",
                loop: true,
              }}
              plugins={[
                Autoplay({
                  delay: 1500,
                })
              ]}
              className="w-full"
            >
              <CarouselContent className="-ml-2 md:-ml-4">
                {clientLogos.map((client, index) => (
                  <CarouselItem key={index} className="pl-2 md:pl-4 basis-1/2 md:basis-1/3 lg:basis-1/4">
                    <div className="p-4">
                      <div className="flex items-center justify-center h-24">
                        <img
                          src={client.logo}
                          alt={client.name ? `${client.name} logo` : ''}
                          className="h-20 w-auto object-contain"
                        />
                      </div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="hidden md:flex" />
              <CarouselNext className="hidden md:flex" />
            </Carousel>
          </div>
        </div>
      </section>

      {/* Proven Growth Section */}
      <section className="py-10 sm:py-12 md:py-16 bg-purple-700 text-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">
              Proven Growth for the Clinic We Work With
            </h2>
            <p className="text-base sm:text-lg md:text-xl opacity-90 max-w-2xl mx-auto">
              See how Fly helped clinics attract more patients, boost calls, and grow appointments.
            </p>
          </div>

          {/* Performance Stats Image */}
          <div className="max-w-6xl mx-auto px-2 sm:px-0">
            <img
              src="/lovable-uploads/healthcare-stats.png"
              alt="Proven Growth Stats - Performance Overview"
              className="w-full h-auto rounded-xl sm:rounded-2xl shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-10 sm:py-12 md:py-16 bg-gradient-to-br from-purple-50 to-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">
              Clinics Rank Higher Locally and Attract More Patient
            </h2>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
              {/* Basic Services */}
              <div className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-8 shadow-lg">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Basic Services</h3>
                <div className="space-y-4 sm:space-y-6">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-base sm:text-lg mb-1 sm:mb-2">Google Profile Audit & Optimization</h4>
                      <p className="text-gray-600 text-sm sm:text-base">
                        Find and fix ranking issues to boost "clinic near me" visibility.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 sm:gap-4">
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-base sm:text-lg mb-1 sm:mb-2">Posts & Updates</h4>
                      <p className="text-gray-600 text-sm sm:text-base">
                        Keep your clinic active with regular health tips and service posts.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 sm:gap-4">
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-base sm:text-lg mb-1 sm:mb-2">Personalized Review Responses</h4>
                      <p className="text-gray-600 text-sm sm:text-base">
                        Build credibility with hyper-personalized, automated review replies.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Benefits */}
              <div className="bg-gradient-to-br from-purple-100 to-purple-50 rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-8 shadow-lg border-2 border-purple-300">
                <h3 className="text-xl sm:text-2xl font-bold text-purple-900 mb-4 sm:mb-6">Additional Benefits</h3>
                <div className="space-y-4 sm:space-y-6">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-purple-700 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-base sm:text-lg mb-1 sm:mb-2 text-purple-900">Landing Page / Microsite</h4>
                      <p className="text-gray-700 text-sm sm:text-base">
                        Convert searches into appointment bookings.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 sm:gap-4">
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-purple-700 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-base sm:text-lg mb-1 sm:mb-2 text-purple-900">Services Listing</h4>
                      <p className="text-gray-700 text-sm sm:text-base">
                        Showcase treatments, packages, or services directly on Google.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 sm:gap-4">
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-purple-700 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-base sm:text-lg mb-1 sm:mb-2 text-purple-900">Citations</h4>
                      <p className="text-gray-700 text-sm sm:text-base">
                        Boost local ranking with consistent listings across directories.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 sm:gap-4">
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-purple-700 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-base sm:text-lg mb-1 sm:mb-2 text-purple-900">Profile Protection</h4>
                      <p className="text-gray-700 text-sm sm:text-base">
                        You receive an email notification about any detected changes on Google Profile.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section className="py-10 sm:py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12 px-2">
            Key benefits that set us apart from other tools
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 max-w-6xl mx-auto">
            {/* Updates & Support Group */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-5 sm:p-6 md:p-8 text-center">
                <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-purple-100 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6">
                  <MessageCircle className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 text-purple-600" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3">Updates & Support Group</h3>
                <p className="text-gray-600 text-sm sm:text-base">
                  Stay instantly informed with real-time updates, review alerts, and new features through our dedicated WhatsApp group.
                </p>
              </CardContent>
            </Card>

            {/* Weekly Performance Reports */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-5 sm:p-6 md:p-8 text-center">
                <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-blue-100 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6">
                  <BarChart3 className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 text-blue-600" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3">Weekly Performance Reports</h3>
                <p className="text-gray-600 text-sm sm:text-base">
                  Get clear, data-driven reports on calls, directions, and profile views, delivered straight to your email every week.
                </p>
              </CardContent>
            </Card>

            {/* Website SEO Services */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-5 sm:p-6 md:p-8 text-center">
                <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-orange-100 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6">
                  <Target className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 text-orange-600" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3">Website SEO Services</h3>
                <p className="text-gray-600 text-sm sm:text-base">
                  Rank higher, load faster, and attract more patients through Google Search.
                </p>
              </CardContent>
            </Card>

            {/* SEO Consultation & Guidance */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-5 sm:p-6 md:p-8 text-center">
                <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-green-100 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6">
                  <Users className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 text-green-600" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3">SEO Consultation & Guidance</h3>
                <p className="text-gray-600 text-sm sm:text-base">
                  Our experts review your Google Profile and rankings to resolve visibility issues and suggest local SEO improvements.
                </p>
              </CardContent>
            </Card>

            {/* Monthly Progress Calls */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-5 sm:p-6 md:p-8 text-center">
                <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-teal-100 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6">
                  <Phone className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 text-teal-600" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3">Monthly Progress Calls</h3>
                <p className="text-gray-600 text-sm sm:text-base">
                  Track growth, discuss new opportunities, and plan next-month goals directly with our optimization specialists.
                </p>
              </CardContent>
            </Card>

            {/* Negative Review Escalation */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <CardContent className="p-5 sm:p-6 md:p-8 text-center">
                <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-red-100 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6">
                  <Shield className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 text-red-600" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3">Negative Review Escalation</h3>
                <p className="text-gray-600 text-sm sm:text-base">
                  Any low rating or complaint is auto-flagged and shared with your clinic team on WhatsApp for immediate action.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-10 sm:py-12 md:py-16 bg-gradient-to-br from-purple-50 to-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <Badge className="bg-purple-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm mb-3 sm:mb-4">PRICING</Badge>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">
              Flexible Plans for <span className="italic">Everyone</span>
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-6 sm:mb-8 px-2">
              Choose a plan that fits your goals and scale as you grow
            </p>

            {/* Currency Toggle */}
            <div className="inline-flex items-center bg-purple-200 rounded-full p-1">
              <button
                onClick={() => setCurrency("INR")}
                className={`px-4 sm:px-6 py-1.5 sm:py-2 rounded-full font-medium transition-all text-sm sm:text-base ${
                  currency === "INR"
                    ? "bg-purple-700 text-white"
                    : "text-gray-700 hover:text-purple-700"
                }`}
              >
                INR
              </button>
              <button
                onClick={() => setCurrency("USD")}
                className={`px-4 sm:px-6 py-1.5 sm:py-2 rounded-full font-medium transition-all text-sm sm:text-base ${
                  currency === "USD"
                    ? "bg-purple-700 text-white"
                    : "text-gray-700 hover:text-purple-700"
                }`}
              >
                Dollars
              </button>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 max-w-6xl mx-auto">
            {plans[currency].map((plan, index) => (
              <Card
                key={index}
                className={`relative border-0 shadow-xl hover:shadow-2xl transition-all duration-300 ${
                  plan.popular ? "ring-2 ring-green-500 sm:transform sm:scale-105" : ""
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 sm:-top-4 left-1/2 transform -translate-x-1/2 z-10">
                    <Badge className="bg-green-500 text-white px-3 sm:px-4 py-0.5 sm:py-1 flex items-center gap-1 text-xs sm:text-sm">
                      <Award className="w-3 h-3 sm:w-4 sm:h-4" />
                      Popular
                    </Badge>
                  </div>
                )}
                <CardContent className="p-5 sm:p-6 md:p-8 bg-gradient-to-br from-purple-700 to-purple-900 text-white rounded-t-xl">
                  <h3 className="text-xl sm:text-2xl font-bold mb-2">{plan.name}</h3>
                  <div className="mb-3 sm:mb-4">
                    <span className="text-2xl sm:text-3xl md:text-4xl font-bold">{plan.price}</span>
                    <span className="text-sm sm:text-base md:text-lg opacity-80">{plan.period}</span>
                  </div>
                  {plan.tagline && (
                    <p className="text-yellow-300 font-medium mb-3 sm:mb-4 text-sm sm:text-base">{plan.tagline}</p>
                  )}
                  {plan.savings && (
                    <p className="text-yellow-300 font-medium mb-3 sm:mb-4 text-sm sm:text-base">{plan.savings}</p>
                  )}
                  <Button
                    className="w-full bg-white text-purple-700 hover:bg-gray-100 font-bold py-2 sm:py-3 rounded-full text-sm sm:text-base"
                    onClick={() => {
                      if (typeof window !== 'undefined') {
                        window.open('https://wa.me/918446712833', '_blank');
                      }
                    }}
                  >
                    {plan.cta} →
                  </Button>
                </CardContent>
                <CardContent className="p-5 sm:p-6 md:p-8">
                  <ul className="space-y-2 sm:space-y-3">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm sm:text-base">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-10 sm:py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-3 sm:mb-4">
            Loved by Clinics & Businesses
          </h2>
          <p className="text-center text-gray-600 mb-8 sm:mb-12 max-w-2xl mx-auto text-sm sm:text-base px-2">
            See how clinics and hospitals across India are growing their patient base with Fly's GMB optimization
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 max-w-6xl mx-auto">
            {/* Osteon Orthopedic Hospital */}
            <Card className="border-0 shadow-lg border-l-4 border-l-purple-600">
              <CardContent className="p-4 sm:p-5 md:p-6">
                <div className="flex gap-1 mb-2 sm:mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <blockquote className="text-gray-700 leading-relaxed mb-3 sm:mb-4 text-sm sm:text-base">
                  "Before Fly, our Google Business Profile wasn't reaching its potential. After optimization, we noticed a steady increase in search impressions and started receiving more calls from patients looking for orthopedic care. The team has been very helpful."
                </blockquote>
                <div className="font-bold text-gray-900 text-sm sm:text-base">Osteon Orthopedic Hospital</div>
                <div className="text-xs sm:text-sm text-gray-500">Orthopedic Specialists</div>
                <div className="mt-2 sm:mt-3 flex gap-1.5 sm:gap-2 flex-wrap">
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 sm:py-1 rounded">Improved Visibility</span>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 sm:py-1 rounded">More Patient Calls</span>
                </div>
              </CardContent>
            </Card>

            {/* Destiny Dental Clinic */}
            <Card className="border-0 shadow-lg border-l-4 border-l-purple-600">
              <CardContent className="p-4 sm:p-5 md:p-6">
                <div className="flex gap-1 mb-2 sm:mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <blockquote className="text-gray-700 leading-relaxed mb-3 sm:mb-4 text-sm sm:text-base">
                  "Fly helped improve our online presence noticeably. Our Google Maps visibility has improved and we're now appearing in more local searches. We've seen a healthy increase in new patient appointments over the past few months."
                </blockquote>
                <div className="font-bold text-gray-900 text-sm sm:text-base">Destiny Dental Clinic</div>
                <div className="text-xs sm:text-sm text-gray-500">Dental Practice</div>
                <div className="mt-2 sm:mt-3 flex gap-1.5 sm:gap-2 flex-wrap">
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 sm:py-1 rounded">Better Local Rankings</span>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 sm:py-1 rounded">More Appointments</span>
                </div>
              </CardContent>
            </Card>

            {/* Sai Arogyam Dental Clinic */}
            <Card className="border-0 shadow-lg border-l-4 border-l-purple-600">
              <CardContent className="p-4 sm:p-5 md:p-6">
                <div className="flex gap-1 mb-2 sm:mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <blockquote className="text-gray-700 leading-relaxed mb-3 sm:mb-4 text-sm sm:text-base">
                  "The GMB optimization by Fly helped us reach more patients in our area. We've seen good growth in profile views and direction requests. The consistent posting and review management has made a real difference for our clinic."
                </blockquote>
                <div className="font-bold text-gray-900 text-sm sm:text-base">Sai Arogyam Dental Clinic</div>
                <div className="text-xs sm:text-sm text-gray-500">Dental Practice</div>
                <div className="mt-2 sm:mt-3 flex gap-1.5 sm:gap-2 flex-wrap">
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 sm:py-1 rounded">More Profile Views</span>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 sm:py-1 rounded">Better Engagement</span>
                </div>
              </CardContent>
            </Card>

            {/* Go-Best Dentist */}
            <Card className="border-0 shadow-lg border-l-4 border-l-purple-600">
              <CardContent className="p-4 sm:p-5 md:p-6">
                <div className="flex gap-1 mb-2 sm:mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <blockquote className="text-gray-700 leading-relaxed mb-3 sm:mb-4 text-sm sm:text-base">
                  "We were struggling with local visibility despite our good location. Fly's optimization helped improve our monthly impressions significantly. We're now getting more phone inquiries and the practice is busier than before."
                </blockquote>
                <div className="font-bold text-gray-900 text-sm sm:text-base">Go-Best Dentist</div>
                <div className="text-xs sm:text-sm text-gray-500">Dental Practice</div>
                <div className="mt-2 sm:mt-3 flex gap-1.5 sm:gap-2 flex-wrap">
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 sm:py-1 rounded">Higher Impressions</span>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 sm:py-1 rounded">More Inquiries</span>
                </div>
              </CardContent>
            </Card>

            {/* Dr. Mutha's Dental Speciality Centre */}
            <Card className="border-0 shadow-lg border-l-4 border-l-purple-600">
              <CardContent className="p-4 sm:p-5 md:p-6">
                <div className="flex gap-1 mb-2 sm:mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <blockquote className="text-gray-700 leading-relaxed mb-3 sm:mb-4 text-sm sm:text-base">
                  "As a specialty dental centre, we needed targeted visibility for specific procedures. Fly helped optimize our GMB profile with the right keywords and services. We've noticed improved search appearance and more relevant patient inquiries."
                </blockquote>
                <div className="font-bold text-gray-900 text-sm sm:text-base">Dr. Mutha's Dental Speciality Centre</div>
                <div className="text-xs sm:text-sm text-gray-500">Dental Specialists</div>
                <div className="mt-2 sm:mt-3 flex gap-1.5 sm:gap-2 flex-wrap">
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 sm:py-1 rounded">Better Visibility</span>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 sm:py-1 rounded">Quality Leads</span>
                </div>
              </CardContent>
            </Card>

            {/* General Testimonial */}
            <Card className="border-0 shadow-lg border-l-4 border-l-purple-600">
              <CardContent className="p-4 sm:p-5 md:p-6">
                <div className="flex gap-1 mb-2 sm:mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <blockquote className="text-gray-700 leading-relaxed mb-3 sm:mb-4 text-sm sm:text-base">
                  "The analytics dashboard gives us useful insights into how patients find us. We can now track which services generate interest and make informed decisions. Our practice has seen steady growth since working with Fly."
                </blockquote>
                <div className="font-bold text-gray-900 text-sm sm:text-base">Healthcare Partner</div>
                <div className="text-xs sm:text-sm text-gray-500">Multi-Specialty Clinic</div>
                <div className="mt-2 sm:mt-3 flex gap-1.5 sm:gap-2 flex-wrap">
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 sm:py-1 rounded">Data-Driven Insights</span>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 sm:py-1 rounded">Steady Growth</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why We're the Right Choice */}
      <section className="py-10 sm:py-12 md:py-16 bg-gradient-to-r from-purple-700 to-purple-900 text-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">Why we're the right choice</h2>
            <p className="text-base sm:text-lg md:text-xl opacity-90 max-w-3xl mx-auto px-2">
              We don't just manage your profile, we make it rank, convert, and bring patients straight to your clinic.
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-xl sm:rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl mb-8 sm:mb-12 mx-2 sm:mx-0">
              <img
                src="/lovable-uploads/healthcare-why-fly.png"
                alt="Why Fly - Google Business Profile Performance Dashboard"
                className="w-full h-auto"
              />
            </div>

            <div className="grid grid-cols-3 gap-4 sm:gap-6 md:gap-8 text-center">
              <div>
                <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-1 sm:mb-2">100%</div>
                <div className="text-xs sm:text-sm md:text-lg lg:text-xl opacity-90">Satisfied Clients</div>
              </div>
              <div>
                <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-1 sm:mb-2">1500+</div>
                <div className="text-xs sm:text-sm md:text-lg lg:text-xl opacity-90">Active Locations</div>
              </div>
              <div>
                <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-1 sm:mb-2">45%</div>
                <div className="text-xs sm:text-sm md:text-lg lg:text-xl opacity-90">Growth Experienced</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-10 sm:py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12 px-2">
              Answers to your most common questions
            </h2>

            <Accordion type="single" collapsible className="space-y-3 sm:space-y-4">
              <AccordionItem value="item-1" className="border-0">
                <Card className="border border-gray-200 rounded-lg sm:rounded-xl hover:shadow-lg transition-shadow">
                  <AccordionTrigger className="px-4 sm:px-6 py-4 sm:py-6 hover:no-underline">
                    <h3 className="text-sm sm:text-base md:text-lg font-semibold text-left pr-2">
                      What exactly does Fly Social do for my Google Business Profile?
                    </h3>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 sm:px-6 pb-4 sm:pb-6">
                    <p className="text-gray-600 text-sm sm:text-base">
                      We optimize your profile, manage posts and reviews, track rankings, and provide actionable insights to help you attract more patients from Google Search and Maps.
                    </p>
                  </AccordionContent>
                </Card>
              </AccordionItem>

              <AccordionItem value="item-2" className="border-0">
                <Card className="border border-gray-200 rounded-lg sm:rounded-xl hover:shadow-lg transition-shadow">
                  <AccordionTrigger className="px-4 sm:px-6 py-4 sm:py-6 hover:no-underline">
                    <h3 className="text-sm sm:text-base md:text-lg font-semibold text-left pr-2">
                      I already have a Google Business Profile. Why do I need this tool?
                    </h3>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 sm:px-6 pb-4 sm:pb-6">
                    <p className="text-gray-600 text-sm sm:text-base">
                      Having a profile is just the start. We help you optimize it to rank higher in local searches, manage reviews professionally, and track performance to continuously improve your visibility and patient acquisition.
                    </p>
                  </AccordionContent>
                </Card>
              </AccordionItem>

              <AccordionItem value="item-3" className="border-0">
                <Card className="border border-gray-200 rounded-lg sm:rounded-xl hover:shadow-lg transition-shadow">
                  <AccordionTrigger className="px-4 sm:px-6 py-4 sm:py-6 hover:no-underline">
                    <h3 className="text-sm sm:text-base md:text-lg font-semibold text-left pr-2">
                      Can this actually help me get more patients?
                    </h3>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 sm:px-6 pb-4 sm:pb-6">
                    <p className="text-gray-600 text-sm sm:text-base">
                      Yes! Our clients typically see 20-40% more calls and inquiries within 60 days. Better rankings, professional review responses, and regular updates help patients find and trust your clinic.
                    </p>
                  </AccordionContent>
                </Card>
              </AccordionItem>

              <AccordionItem value="item-4" className="border-0">
                <Card className="border border-gray-200 rounded-lg sm:rounded-xl hover:shadow-lg transition-shadow">
                  <AccordionTrigger className="px-4 sm:px-6 py-4 sm:py-6 hover:no-underline">
                    <h3 className="text-sm sm:text-base md:text-lg font-semibold text-left pr-2">
                      Will I still have control over my Google profile?
                    </h3>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 sm:px-6 pb-4 sm:pb-6">
                    <p className="text-gray-600 text-sm sm:text-base">
                      Absolutely. You retain full ownership and control. We work with your approval and keep you informed of all changes and optimizations we make.
                    </p>
                  </AccordionContent>
                </Card>
              </AccordionItem>

              <AccordionItem value="item-5" className="border-0">
                <Card className="border border-gray-200 rounded-lg sm:rounded-xl hover:shadow-lg transition-shadow">
                  <AccordionTrigger className="px-4 sm:px-6 py-4 sm:py-6 hover:no-underline">
                    <h3 className="text-sm sm:text-base md:text-lg font-semibold text-left pr-2">
                      How does Fly Social reply to reviews?
                    </h3>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 sm:px-6 pb-4 sm:pb-6">
                    <p className="text-gray-600 text-sm sm:text-base">
                      We use AI-powered responses customized to your brand voice. Every reply is professional, empathetic, and designed to build trust with potential patients while addressing feedback constructively.
                    </p>
                  </AccordionContent>
                </Card>
              </AccordionItem>

              <AccordionItem value="item-6" className="border-0">
                <Card className="border border-gray-200 rounded-lg sm:rounded-xl hover:shadow-lg transition-shadow">
                  <AccordionTrigger className="px-4 sm:px-6 py-4 sm:py-6 hover:no-underline">
                    <h3 className="text-sm sm:text-base md:text-lg font-semibold text-left pr-2">
                      How will I track what's happening?
                    </h3>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 sm:px-6 pb-4 sm:pb-6">
                    <p className="text-gray-600 text-sm sm:text-base">
                      You'll receive weekly performance reports via email showing calls, directions, profile views, and more. Plus, you get access to our dashboard for real-time insights and monthly progress calls with our specialists.
                    </p>
                  </AccordionContent>
                </Card>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-10 sm:py-12 md:py-16 bg-gradient-to-br from-purple-100 to-purple-50" data-section="contact-form">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Left Side - WhatsApp CTA */}
              <div className="bg-gradient-to-br from-purple-700 to-purple-900 rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 text-white text-center">
                <div className="mb-6 sm:mb-8">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-2xl">
                    <svg
                      className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 text-white"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                  </div>
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">Get in touch with our team</h2>
                  <p className="text-base sm:text-lg md:text-xl opacity-90 mb-6 sm:mb-8">
                    Chat with our experts on WhatsApp and get personalized guidance for your healthcare practice
                  </p>
                </div>

                <Button
                  size="lg"
                  className="bg-green-500 hover:bg-green-600 text-white font-bold px-6 sm:px-8 md:px-12 py-4 sm:py-5 md:py-6 text-base sm:text-lg md:text-xl rounded-full shadow-2xl transform hover:scale-105 transition-all duration-200 w-full sm:w-auto"
                  onClick={() => {
                    if (typeof window !== 'undefined') {
                      window.open('https://wa.me/918446712833?text=Hi%2C%20I%27m%20interested%20in%20learning%20more%20about%20Fly%20Social%20for%20my%20healthcare%20practice', '_blank');
                    }
                  }}
                >
                  <svg
                    className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 mr-2 sm:mr-3 inline-block"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  Chat on WhatsApp
                </Button>

                <p className="text-xs sm:text-sm opacity-75 mt-4 sm:mt-6">
                  Response time: Usually within minutes
                </p>
              </div>

              {/* Right Side - Image */}
              <div className="hidden lg:block">
                <img
                  src="/lovable-uploads/contact-us.avif"
                  alt="Contact Us - Expert Team Support"
                  className="w-full h-auto rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HealthcareLanding;
